import { Avatar, Box, Grid, Link } from "@mui/material";
import Flex from "../../../components/Flex";
import MultiSpecialty from "../../../components/MultiSpecialty";
import OutlineCard from "../../../components/OutlineCard";
import ProfileViewer from "../../../components/ProfileViewer";
import { CustomTrimmedTooltip } from "../../../components/RenderWithTooltip";
import Text from "../../../components/Text";
import SignatureModal from "../../../components/app/admin/provider-groups/SignatureModal";
import { GENDER_RECORD } from "../../../constants";
import useDisclosure from "../../../hooks/use-disclosure";
import { useGetSignatureByProviderID } from "../../../queries/signature/use-get-signature-by-providerID";
import CopyButton from "../../../utils/util-components/CopyButton";
import { getXtenantId } from "../../../utils/utils";
import SignatureSection from "./ProfileSignature";
import BasicInformation from "../../../components/app/provider/BasicInformation";
import LicensedAndDeaInformation from "../../../components/app/provider/LicensedAndDeaInformation";
import { ScrollArea } from "../../../components/ScrollBar";
import CoSignerInformation from "@/components/app/provider/CoSignerInformation";

const ProviderProfileFullView = ({ providerData }: { providerData?: any }) => {
  const isCoSigner = providerData?.baseRole === "SUPERVISING_MD";

  // const insurances = Object.keys(providerData?.acceptedInsurances ?? {})?.length
  //   ? Object.values(providerData?.acceptedInsurances)?.map(
  //       (it, ind) => (ind + 1)?.toString() + ". " + it?.toString()
  //     )
  //   : "-";
  const signatureDisclosure = useDisclosure();

  // const workLocations = Object?.keys(providerData?.workLocations ?? {})?.length
  //   ? providerData?.workLocations
  //   : "-";
  // console.log("providerData ->", providerData);

  const { data: signatureData } = useGetSignatureByProviderID({
    providerUuid: providerData?.uuid,
    xTENANTID: getXtenantId(),
  });
  // console.log("signatureData-", signatureData);

  const PANEL_HEIGHT = "calc(100vh - 15rem)";

  return (
    <Flex gap={"10px"} alignItems={"stretch"} height={PANEL_HEIGHT}>
      <Flex flex={"0 0 30%"} minWidth={0}>
        <OutlineCard
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: 0,
          }}
        >
          <ScrollArea height={"100%"}>
            <Flex gap={"16px"} flexDirection={"column"} px={"24px"} py={"16px"}>
              <Grid item>
                <Avatar
                  sx={{
                    width: "140px",
                    height: "135px",
                  }}
                  src={providerData?.avatar}
                />
                <Text fontWeight={"bold"} mt={"16px"} size={"16px"}>
                  {(providerData?.firstName ?? "") +
                    " " +
                    (providerData?.lastName ?? "")}
                </Text>

                <MultiSpecialty
                  specialties={providerData?.specialities}
                  width={280}
                />
              </Grid>
              <ProfileViewer
                withoutColn
                gap={"10px"}
                firstColW={4}
                secondColW={7.5}
                data={{
                  role: providerData?.role,
                  ...(!isCoSigner && {
                    "Assigned Co-Signer": {
                      custom: () => (
                        <Flex flexDirection="column">
                          <Text>
                            {providerData?.cosigner
                              ? Object.values<string>(providerData.cosigner)[0]
                              : "-"}
                          </Text>
                          <Text color="#1A1A1A80" fontSize="12px">
                            Every visit note you sign is sent to this Co-Signer.
                          </Text>
                        </Flex>
                      ),
                    },
                  }),
                  provider_type: providerData?.providerType,
                  gender: GENDER_RECORD[providerData?.gender],
                  contact_number: providerData?.phone,
                  email: providerData?.email,
                  "NPI Number": providerData?.npi?.toString(),
                  bio: providerData?.bio,
                  Login_URL: {
                    custom: () => (
                      <Flex alignItems={"center"} minWidth={0}>
                        <Link
                          underline="hover"
                          sx={{ overflow: "hidden", minWidth: 0, flex: 1 }}
                        >
                          <a
                            href={providerData?.loginUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <CustomTrimmedTooltip
                              value={providerData?.loginUrl}
                            />
                          </a>
                        </Link>

                        <CopyButton
                          onClick={() => {
                            navigator.clipboard.writeText(
                              providerData?.loginUrl
                            );
                          }}
                        />
                      </Flex>
                    ),
                  },
                }}
              />
              <Box>
                <Flex flexDirection={"column"}>
                  <Text variant="h4" fontWeight={"medium"}>
                    Signature
                  </Text>
                  <SignatureSection
                    signatureData={
                      (signatureData?.data as any)?.providerSignature
                    }
                    onActionClick={() =>
                      signatureDisclosure.open(signatureData?.data)
                    }
                  />
                </Flex>
              </Box>
              <SignatureModal
                providerUuid={providerData?.uuid}
                disclosureHost={signatureDisclosure}
                title="Add Signature"
              />
            </Flex>
          </ScrollArea>
        </OutlineCard>
      </Flex>

      <Flex flex={1} flexDirection={"column"} gap={"10px"} minWidth={0}>
        <ScrollArea height={"100%"}>
          <BasicInformation providerData={providerData} />

          <LicensedAndDeaInformation
            licenceInformation={providerData?.licenceInformation ?? []}
            deaInformation={providerData?.deaInformation ?? []}
          />

          {isCoSigner && (
            <CoSignerInformation
              assignedProviders={providerData?.assignedProviders}
              cosignPin={providerData?.cosignPin}
              cosignPinUpdatedAt={providerData?.cosignPinUpdatedAt}
            />
          )}
        </ScrollArea>
      </Flex>
    </Flex>
  );
};

export default ProviderProfileFullView;
